import Select from 'react-select-2';
import type { FunctionComponent } from 'react';

import FormLabel from '../../../generic/Forms/FormLabel';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import type { SelectOption } from '../../../../types';

import type {
  ExportFieldWithOnChange,
  JiraExportFieldInput,
  JiraExportFieldMultiSelect,
  JiraExportFieldSelect,
  WithOnChange,
} from './useJiraExport';

/**
 * A dropdown/single-select style field.
 */
const ModalIssueExportFieldSelect: FunctionComponent<WithOnChange<JiraExportFieldSelect>> = ({
  id,
  label,
  value,
  options,
  onChange,
}) => (
  <FormLabel
    label={label}
    htmlFor={id}
  >
    <Select
      id={id}
      value={value}
      options={options}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - react-select's types are broken/they don't export the correct types.
      onChange={({ value: nextValue }: SelectOption<string>) => onChange({ id, value: nextValue })}
      isDisabled={options.length === 1}
    />
  </FormLabel>
);

/**
 * A dropdown/single-select style field.
 */
const ModalIssueExportFieldMultiSelect: FunctionComponent<WithOnChange<JiraExportFieldMultiSelect>> = ({
  id,
  label,
  value,
  options,
  onChange,
}) => (
  <FormLabel
    label={label}
    htmlFor={id}
  >
    <Select
      id={id}
      value={value}
      options={options}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - react-select's types are broken/they don't export the correct types.
      onChange={(nextValues: Array<SelectOption<string>>) =>
        onChange({ id, value: nextValues.map(({ value: nextLabel }) => nextLabel) })}
      isClearable={false}
      isMulti
    />
  </FormLabel>
);

/**
 *  Represents a single Jira text input (from customFields).
 */
const ModalIssueExportFieldInput: FunctionComponent<WithOnChange<JiraExportFieldInput>> = ({
  id,
  label,
  value,
  onChange,
}) => (
  <LabeledTextInput
    labelVariant="legacy"
    label={label}
    placeholder={label}
    value={value}
    onChange={(nextValue: string) => onChange({ value: nextValue, id })}
  />
);

/**
 * Represents a single form field in the Jira export modal.
 * Really just switches on the type and renders a field
 * specific to that field type.
 */
const ModalIssueExportField: FunctionComponent<ExportFieldWithOnChange> = (props) => {
  /* eslint-disable react/jsx-props-no-spreading */
  switch (props.type) {
    case 'select':
      return <ModalIssueExportFieldSelect {...props} />;
    case 'multi-select':
      return <ModalIssueExportFieldMultiSelect {...props} />;
    case 'input':
      return <ModalIssueExportFieldInput {...props} />;
    default:
      return null;
  }
  /* eslint-enable react/jsx-props-no-spreading */
};

export default ModalIssueExportField;
